import moment from 'moment/moment';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EmploymentDetailType,
  ExtendedEmploymentDetailType,
} from '~/data/employment/useEmploymentDetails';
import { Enum } from '~/data/enums';
import { EmploymentDetailsProposalProps } from '~/data/proposals';
import {
  getTranslationKeyForObjectKey,
  scheduledDateFormats,
} from '~/features/ScheduledEmploymentChanges/helpers';
import { useResolveUpcomingChanges } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Common/EmploymentUpcomingChanges';
import { emplDetailsNewTypeToLegacy } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/data/newTypeToLegacy';
import { PersonnelGroupValues } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/useScheduleEmploymentDetail';
import { PendingScheduleChanges } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';

export enum ObjectKeyTranslation {
  'fEmplDetailCalcHoursBalance' = 'laCalcHoursBalance',
  'fEmplDetailJobGrade' = 'fJobGrade',
  'fEmplDetailPersonnelGroup1' = 'fPersonnelGroup1',
  'fEmplDetailPersonnelGroup2' = 'fPersonnelGroup2',
  'fEmplDetailPersonnelGroup3' = 'fPersonnelGroup3',
  'fEmplDetailPersonnelGroup4' = 'fPersonnelGroup4',
  'fEmplDetailPersonnelGroup5' = 'fPersonnelGroup5',
  'fEmplDetailStatus' = 'laEmploymentStatus',
  'fEmplDetailTaxNumber' = 'fEmplDetailTaxNumber',
  'fEmplDetailVacBalCalcSixDays' = 'laVacBalCal',
}

export const EmpDetailKeys = [
  'fEmplId',
  'fEmplDetailStartDate',
  'fEmplDetailStatus',
  'fEmplDetailJobGrade',
  'fEmplDetailTaxNumber',
  'fEmplDetailPersonnelGroup1',
  'fEmplDetailPersonnelGroup2',
  'fEmplDetailPersonnelGroup3',
  'fEmplDetailPersonnelGroup4',
  'fEmplDetailPersonnelGroup5',
  'fEmplDetailVacBalCalcSixDays',
  'fEmplDetailCalcHoursBalance',
  'fEmplDetailIsPending',
  'fEmplSubEnteredTime',
];

type PropsType = {
  proposals: EmploymentDetailsProposalProps[];
  defaultData: EmploymentDetailType;
  proposalWorkflowsEnabled: boolean;
  pendingScheduleChanges: {
    index: number;
    pendingData?: PendingScheduleChanges[];
  };
  fieldValueFormatProps: {
    jobGrades: Enum[];
    getPersonnelGroupOptions: (
      groupNumber: number,
      personnelGroupValues?: PersonnelGroupValues,
    ) => Enum[];
    employmentStatuses: Enum[];
  };
};

export const useUpcomingScheduledEmploymentDetail = (props: PropsType) => {
  const {
    proposals,
    proposalWorkflowsEnabled,
    defaultData,
    pendingScheduleChanges,
    fieldValueFormatProps,
  } = props;

  const { t } = useTranslation();
  const { resolveUpcomingChanges, resolveProposalsChangesSequence } =
    useResolveUpcomingChanges();

  const attributeExceptions = useMemo(
    () => [
      'fEmplDetailIsPending',
      'fEmplDetailStartDate',
      'fEmplSubEnteredTime',
      'fEmplId',
    ],
    [],
  );

  const getEnumValueName = useCallback(
    (array: Enum[], value: string) =>
      array.find((o) => o.code === value)?.name || '',
    [],
  );

  const translateFieldName = useCallback(
    (key: string): string => {
      const translationKey = getTranslationKeyForObjectKey<
        typeof ObjectKeyTranslation
      >(key, ObjectKeyTranslation);

      return t(translationKey);
    },
    [t],
  );

  const formatFieldValue = useCallback(
    (key: string, value?: number | boolean | string): string => {
      const { jobGrades, employmentStatuses, getPersonnelGroupOptions } =
        fieldValueFormatProps;
      if (typeof value === 'undefined') {
        return '';
      }
      switch (key) {
        case 'fEmplDetailCalcHoursBalance':
          return value.toString();
        case 'fEmplDetailJobGrade':
          return getEnumValueName(jobGrades, value as string);
        case 'fEmplDetailStatus':
          return getEnumValueName(employmentStatuses, value as string);
        case 'fEmplDetailTaxNumber':
          return value.toString();
        case 'fEmplDetailVacBalCalcSixDays':
          return value.toString();
        default: {
          const fieldOptions = getPersonnelGroupOptions(
            parseInt(key.charAt(key.length - 1)),
          );
          return getEnumValueName(fieldOptions, value as string);
        }
      }
    },
    [getEnumValueName, fieldValueFormatProps],
  );

  const resolveUpcoming = useCallback(
    (oldData: EmploymentDetailType, newData: EmploymentDetailType) => {
      return resolveUpcomingChanges({
        standardFields: {
          fieldKeys: EmpDetailKeys,
          fieldKeyExceptions: attributeExceptions,
          oldData,
          newData,
          labelTranslator: translateFieldName,
          valueFormatter: formatFieldValue,
        },
        customFields: {
          customEnumGroupKey: 'CUSTOM_ENTITY_EMPLOYMENT_DETAILS',
          customDataEntity: 'EMPLOYMENT_DETAILS',
          oldData: oldData.fEmplDetailCustomData,
          newData: newData.fEmplDetailCustomData,
        },
      });
    },
    [
      resolveUpcomingChanges,
      attributeExceptions,
      translateFieldName,
      formatFieldValue,
    ],
  );

  const upcomingChanges = useMemo(() => {
    const { index, pendingData } = pendingScheduleChanges;
    if (!!pendingData?.[index]?.employmentDetails && defaultData) {
      const previousEmpDetails = pendingData?.[index - 1]?.employmentDetails;
      let oldArray: ExtendedEmploymentDetailType =
        index === 0 || !previousEmpDetails ? defaultData : previousEmpDetails;
      let newArray: ExtendedEmploymentDetailType =
        pendingData?.[index]?.employmentDetails;

      return resolveUpcoming(oldArray, newArray);
    }
    return [];
  }, [defaultData, pendingScheduleChanges, resolveUpcoming]);

  const proposalChanges = useMemo(() => {
    if (!proposalWorkflowsEnabled || proposals.length < 1) {
      return {
        upcoming: [],
        onGoing: undefined,
      };
    }

    const labelResolver = (prop: EmploymentDetailsProposalProps) =>
      moment(prop.proposalData.ed.start_date).format(
        scheduledDateFormats.dotFormat,
      );

    return resolveProposalsChangesSequence({
      proposals,
      proposalToLegacyProps: emplDetailsNewTypeToLegacy,
      defaultData,
      labelResolver,
      valueChangesResolver: resolveUpcoming,
    });
  }, [
    proposalWorkflowsEnabled,
    proposals,
    defaultData,
    resolveUpcoming,
    resolveProposalsChangesSequence,
  ]);

  return {
    proposalChanges,
    upcomingChanges,
  };
};
